import { useEffect, useRef } from 'react'
import { createChart, HistogramSeries, LineSeries, LineStyle } from 'lightweight-charts'
import { CarsRegimePrimitive, buildSignalRegimeSpans } from '../../lib/chart/CarsRegimePrimitive.js'
import { updateChartSeries } from '../../lib/marketData/marketHealthSeries.js'

import { BREADTH_TINT, REGIME_START_TINT, REGIME_TINT as tint } from '../../lib/marketExposure/chartTone.js'

const EMPTY = []

export default function MarketHealthChart({ data = EMPTY, average = EMPTY, regimes = EMPTY, histogram = false, purple = false, visibleRange = null, regimePalette = 'breadth', label }) {
  const container = useRef(null)
  const api = useRef(null)
  useEffect(() => {
    const chart = createChart(container.current, {
      autoSize: false,
      layout: { background: { color: 'transparent' }, textColor: '#909399', fontSize: 9, attributionLogo: false },
      grid: { vertLines: { visible: false }, horzLines: { visible: false } },
      rightPriceScale: { visible: histogram, minimumWidth: 72, borderVisible: false, ticksVisible: false, entireTextOnly: true },
      leftPriceScale: { visible: false },
      timeScale: { borderVisible: false, visible: !histogram, rightOffset: 2, minBarSpacing: 1, lockVisibleTimeRangeOnResize: true },
      crosshair: { vertLine: { labelVisible: !histogram, color: '#88888855' }, horzLine: { visible: false, labelVisible: false } },
      handleScale: !histogram, handleScroll: !histogram,
    })
    const resize = new ResizeObserver(entries => {
      for (const {contentRect} of entries) if(contentRect?.width>0&&contentRect?.height>0) chart.resize(Math.floor(contentRect.width),Math.floor(contentRect.height))
    })
    resize.observe(container.current)
    const series = chart.addSeries(histogram ? HistogramSeries : LineSeries, {
      color: '#949494', lineWidth: 1, lastValueVisible: false, priceLineVisible: false,
      priceFormat: histogram ? { type: 'custom', formatter: () => '' } : { type: 'price', precision: 4, minMove: 0.0001 },
      crosshairMarkerVisible: !histogram,
    })
    const ma = chart.addSeries(LineSeries, { color: purple ? '#a78bfa' : '#37ad83', lineWidth: 1, lineStyle: LineStyle.Dashed, lastValueVisible: false, priceLineVisible: false, crosshairMarkerVisible: false })
    const positive = new CarsRegimePrimitive()
    const negative = new CarsRegimePrimitive()
    const neutral = new CarsRegimePrimitive()
    series.attachPrimitive(positive)
    series.attachPrimitive(negative)
    series.attachPrimitive(neutral)
    if (histogram) series.createPriceLine({ price: 0, color: '#88888866', lineWidth: 1, lineStyle: LineStyle.Dotted, axisLabelVisible: false })
    const applyTheme = () => {
      const color = getComputedStyle(container.current).color
      chart.applyOptions({ layout: { textColor: color } })
      if (!histogram) series.applyOptions({ color })
    }
    applyTheme()
    const observer = new MutationObserver(applyTheme)
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'style'] })
    api.current = { chart, series, ma, positive, negative, neutral, previous: [], previousAverage: [], fitted: false }
    return () => { resize.disconnect(); observer.disconnect(); api.current = null; chart.remove() }
  }, [histogram, purple])

  useEffect(() => {
    const state = api.current
    if (!state) return
    const range = state.chart.timeScale().getVisibleLogicalRange()
    const previousFirst = state.previous[0]?.time
    const firstDayChanged = previousFirst !== data[0]?.time
    const mode = updateChartSeries(state.series, state.previous, data)
    updateChartSeries(state.ma, state.previousAverage, average)
    state.previous = data
    state.previousAverage = average
    state.positive.update({ spans: buildSignalRegimeSpans(regimes,1), startFillStyle:purple?REGIME_START_TINT.purple:REGIME_START_TINT.positive, fillStyle: purple ? tint.purple : regimePalette==='credit'?tint.positive:BREADTH_TINT.positive })
    state.negative.update({ spans: purple?[]:buildSignalRegimeSpans(regimes,-1), startFillStyle:REGIME_START_TINT.negative, fillStyle: regimePalette==='credit'?tint.negative:BREADTH_TINT.negative })
    state.neutral.update({ spans: regimePalette==='credit'?buildSignalRegimeSpans(regimes,0):[], startFillStyle:REGIME_START_TINT.neutral, fillStyle: tint.neutral })
    const hasValues = data.some(point => Number.isFinite(point.value))
    if (!hasValues) state.fitted = false
    if (hasValues && !state.fitted) {
      // Reserve one quarter of the default viewport for future sessions.
      const visibleBars = Math.min(63, data.length)
      state.chart.timeScale().setVisibleLogicalRange({ from: data.length - visibleBars, to: data.length - 1 + visibleBars / 3 })
      state.fitted = true
    } else if (hasValues && mode === 'replace') {
      // Prepending history shifts logical indices. Preserve both the viewed
      // dates and any future space instead of clamping to a dated range.
      const shift = firstDayChanged ? data.findIndex(point => point.time === previousFirst) : 0
      if (range && shift >= 0) state.chart.timeScale().setVisibleLogicalRange({ from: range.from + shift, to: range.to + shift })
    }
    if (histogram && visibleRange && hasValues) state.chart.timeScale().setVisibleRange(visibleRange)
  }, [data, average, regimes, purple, histogram, visibleRange, regimePalette])

  return <div ref={container} className="h-full w-full min-w-0 text-inkMuted" role="img" aria-label={label} />
}
